/**
 * Browser-time fetch of the sitewide alert (Contentful `sitewideAlert`): the
 * message bar (icon, title, message, optional link) shown above the content of
 * every page.
 *
 * Position:
 * - Called **in the browser**, not at build, by
 *   `src/components/Shared/AlertBar/AlertBar.tsx` on mount and on every language
 *   change. `AlertBar` is rendered by `src/components/NextLayouts/HeaderLayout.tsx`
 *   and applies the page, location and dismissal rules; see its header. So an
 *   editor's change shows on the next full page load, without a rebuild.
 * - Requests go through `@ryerson/frontend.contentful` → `fetchContentfulData`
 *   with the `NEXT_PUBLIC_CONTENTFUL_*` settings baked into the browser bundle
 *   (the Delivery token is public by design, web-architecture ADR-0038). It makes
 *   up to three attempts, six seconds apart, before it throws.
 * - Shaped by `./helpers/index.ts` → `processContentfulSitewideAlertResponse`.
 *   Pinned by `./getContentfulSitewideAlert.spec.ts`.
 *
 * Rules a change must keep:
 * - Contentful does the selection: the one alert for this brand whose start and
 *   end dates include now, newest start first. Only one alert can show at a time.
 * - The `company` value for this content type is `Ryerson` or `STS`, not
 *   `Southern Tool Steel` as on most other content types.
 * - A failure is logged and rethrown. `AlertBar.tsx` doesn't catch it, so the bar
 *   stays hidden. web-architecture ADR-0033 §3 asks browser-side calls to catch
 *   and log.
 */
import {
	ContentfulAPIResponse,
	fetchContentfulData,
	getLocaleByLanguage,
	getContentfulCompanyStringFromCompanyEnvVariable,
} from "@ryerson/frontend.contentful";
import { Language } from "@ryerson/frontend.common";
import {
	ContentfulSitewideAlertAPIResponseFields,
	ProcessedContentfulSitewideAlertFields,
} from "./types";
import { processContentfulSitewideAlertResponse } from "./helpers";

// The last result, for this page load: the shaped alert (which carries the locale it was fetched
// in), `"empty"` when Contentful returned none, or `null` before the first successful call. A
// failed call leaves it as it was.
let cachedAlert: (ProcessedContentfulSitewideAlertFields & { locale: string }) | null | "empty" =
	null;

// If function is called on the client side, clear the cache on page refresh / new tab
// The listener runs as this page unloads (a reload, or leaving the site); client-side navigation
// keeps the cache. A new tab loads its own copy of the module, with its own empty cache.
if (typeof window !== "undefined") {
	window.addEventListener("beforeunload", () => {
		cachedAlert = null;
	});
}

/**
 * Fetch the latest Contentful active site-wide alert.
 *
 * Returns the cached alert when it was fetched in `language`'s locale, and
 * fetches again for another language. Once a call has found no alert, every
 * later call in the page load returns `null` without a request, whatever the
 * language.
 *
 * @param language en | fr | es
 * @returns Active alert object or null if none found
 * @throws The Contentful or network error, after logging the function name. On
 *   a build whose `NEXT_PUBLIC_COMPANY` is neither `ryerson` nor
 *   `southernToolSteel`, `getContentfulCompanyStringFromCompanyEnvVariable`
 *   throws instead.
 */
const getContentfulSitewideAlert = async function (
	language: Language
): Promise<ProcessedContentfulSitewideAlertFields | null> {
	try {
		if (cachedAlert !== null) {
			if (cachedAlert === "empty") {
				// "empty" means that the initial API call returned no results, return null to prevent further API calls
				return null;
			} else if (cachedAlert.locale === getLocaleByLanguage(language)) {
				// if the previously grabbed alert is in the same locale as the current request, return it
				return cachedAlert;
			}
		}

		const currentDate = new Date().toISOString();

		const response = await fetchContentfulData<
			ContentfulAPIResponse<ContentfulSitewideAlertAPIResponseFields>
		>("sitewideAlert", [
			{
				name: "locale",
				value: getLocaleByLanguage(language),
			},
			{
				name: "fields.startDate[lte]",
				value: currentDate,
			},
			{
				name: "fields.endDate[gte]",
				value: currentDate,
			},
			{
				name: "fields.company[in]",
				value:
					process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
						? "STS" // this content type has STS for company, not Southern Tool Steel like majority of other content types
						: getContentfulCompanyStringFromCompanyEnvVariable(
								process.env.NEXT_PUBLIC_COMPANY as any,
								true
						  ),
			},
			{ name: "order", value: "-fields.startDate" }, // order by start date descending
			{ name: "limit", value: "1" }, // only need 1 item
		]);

		if (response.data.items && response.data.items.length > 0) {
			cachedAlert = processContentfulSitewideAlertResponse(response);
			return cachedAlert;
		} else {
			cachedAlert = "empty"; // cache "empty" state to prevent unnecessary API calls in case of no alerts from Contentful
			return null;
		}
	} catch (error: any) {
		console.error("Error calling getContentfulSitewideAlert");
		throw error;
	}
};

export default getContentfulSitewideAlert;
